import {
  type ArdoqId,
  type BubbleChartWidgetDatasource,
  type FilterInterfaceFilter,
  type FormattingFilter,
  type TraversalLoadedState,
  type UnsavedBubbleChartWidgetAttributes,
  type WidgetLayout,
  traversalParamsToLoadTraversalParams,
  WidgetDataSourceTypes,
  WidgetTypes,
} from '@ardoq/api-types';
import type {
  BubbleChartSelectedFieldInfo,
  BubbleChartViewSettings,
} from '@ardoq/bubble-chart';
import { getLayoutForNewWidget } from '@ardoq/dashboard';
import type { BuildWidget } from 'dashboard/addToDashboard/types';

const BUBBLE_CHART_WIDGET_ROW_SPAN = 2;

const resolveFieldName = (
  field: string | BubbleChartSelectedFieldInfo | null
): string | undefined => {
  if (field === null) return undefined;
  if (typeof field === 'string') return field || undefined;
  return field.fieldName || undefined;
};

/**
 * Builds the datasource for a bubble chart dashboard widget from the current traversal loaded
 * state. The datasource carries the traversal execution payload inline; the backend re-runs it on
 * every render, so no traversal entity is created and the data stays fresh.
 */
export const buildBubbleChartDatasource = (
  loadedState: TraversalLoadedState
): BubbleChartWidgetDatasource => ({
  sourceType: WidgetDataSourceTypes.INLINE_TRAVERSAL,
  traversalDefinition: traversalParamsToLoadTraversalParams(loadedState.data),
});

type BuildBubbleChartWidgetArgs = {
  name: string;
  datasource: BubbleChartWidgetDatasource;
  viewSettings: BubbleChartViewSettings;
  selectedComponentTypeIds: ArdoqId[];
  layout: Omit<WidgetLayout, 'i'>;
  conditionalFormatting: (FormattingFilter | FilterInterfaceFilter)[];
  workspaceIds: ArdoqId[];
  xAxisTitle?: string;
  yAxisTitle?: string;
  selectedComponentIds?: string[];
  resolvedComponentColors?: Record<string, string>;
  resolvedLabels?: Record<string, string>;
};

const buildBubbleChartWidget = ({
  name,
  datasource,
  viewSettings,
  selectedComponentTypeIds,
  layout,
  conditionalFormatting,
  workspaceIds,
  xAxisTitle,
  yAxisTitle,
  selectedComponentIds,
  resolvedComponentColors,
  resolvedLabels,
}: BuildBubbleChartWidgetArgs): UnsavedBubbleChartWidgetAttributes => {
  const xFieldName = resolveFieldName(viewSettings.selectedFieldNameX);
  const yFieldName = resolveFieldName(viewSettings.selectedFieldNameY);
  const radiusFieldName = resolveFieldName(
    viewSettings.selectedFieldNameRadius
  );

  return {
    name,
    viewType: WidgetTypes.BUBBLE_CHART,
    layout: { ...layout, h: BUBBLE_CHART_WIDGET_ROW_SPAN },
    // Positional: [x, y] or [x, y, radius]. Keep empty slots so an unset axis
    // doesn't shift the others — the reader maps fields[0]=X, fields[1]=Y, fields[2]=radius.
    fields: [
      xFieldName ?? '',
      yFieldName ?? '',
      ...(radiusFieldName ? [radiusFieldName] : []),
    ],
    datasource,
    accessWorkspaceIds: workspaceIds,
    xAxisTitle,
    yAxisTitle,
    selectedComponentTypeIds,
    selectedComponentIds,
    resolvedComponentColors,
    resolvedLabels,
    background: viewSettings.background,
    customQuadrantSettings: viewSettings.customQuadrantSettings,
    showBubblesWithZeroValue: viewSettings.showBubblesWithZeroValue,
    conditionalFormatting,
    bubbleAreaScaleFactor: viewSettings.bubbleAreaScaleFactor,
  };
};

/**
 * Everything the bubble chart widget builder needs, collected from the current view at the moment
 * the dialog opens (view settings, workspace ids, resolved colors/labels, conditional formatting).
 */
export type BubbleChartWidgetContext = {
  loadedState: TraversalLoadedState;
  viewSettings: BubbleChartViewSettings;
  selectedComponentTypeIds: ArdoqId[];
  workspaceIds: ArdoqId[];
  conditionalFormatting: (FormattingFilter | FilterInterfaceFilter)[];
  xAxisTitle?: string;
  yAxisTitle?: string;
  selectedComponentIds?: ArdoqId[];
  resolvedComponentColors: Record<string, string>;
  resolvedLabels?: Record<string, string>;
};

export const createBubbleChartBuildWidget =
  (context: BubbleChartWidgetContext): BuildWidget =>
  ({ widgetName, existingWidgets }) => {
    return {
      ok: true,
      widget: buildBubbleChartWidget({
        name: widgetName,
        datasource: buildBubbleChartDatasource(context.loadedState),
        viewSettings: context.viewSettings,
        selectedComponentTypeIds: context.selectedComponentTypeIds,
        layout: getLayoutForNewWidget(
          existingWidgets,
          WidgetTypes.BUBBLE_CHART
        ),
        conditionalFormatting: context.conditionalFormatting,
        workspaceIds: context.workspaceIds,
        xAxisTitle: context.xAxisTitle,
        yAxisTitle: context.yAxisTitle,
        selectedComponentIds: context.selectedComponentIds,
        resolvedComponentColors: context.resolvedComponentColors,
        resolvedLabels: context.resolvedLabels,
      }),
    };
  };
